import { Link } from "react-router-dom";
import { PageBanner } from "@/components/site/PageBanner";
import { Team } from "@/components/sections/Team";
import { useSeo } from "@/hooks/useSeo";
import { useSiteConfig } from "@/lib/site-config";

const ABOUT_COVER = "/images/site/office.jpg";

// Brand values — copy is the source-of-truth content from
// HiveHomes_Website_Content.docx (About Page → "What We Stand For").
const VALUES = [
  {
    title: "Expertise You Can Trust",
    body: "Dubai's property market moves fast. Our advisors bring years of on-the-ground knowledge so every recommendation you receive is informed, honest, and built around your best interest.",
  },
  {
    title: "Personal, Not Transactional",
    body: "We do not close deals and move on. We build relationships. From your first enquiry to long after completion, we remain your trusted point of contact in Dubai real estate.",
  },
  {
    title: "Discretion and Care",
    body: "Whether you are buying your first home or expanding a portfolio, every conversation with Hive Homes is handled with complete confidentiality and the highest standard of personal care.",
  },
];

export function AboutPage() {
  const siteConfig = useSiteConfig();
  const reraOrn = siteConfig.reraOrn?.set ? siteConfig.reraOrn.display : "42280";

  useSeo({
    title: "About Hive Homes | Dubai Boutique Real Estate Brokerage",
    description:
      "Hive Homes is Dubai's boutique real estate brokerage. Meet our team, learn our story, and discover why buyers, sellers and investors trust us with their most important decisions.",
  });

  return (
    <>
      {/* Hero — client banner photo behind the page heading, matching
          the Properties / Insights / Communities / Contact heroes. The
          office photo is bright, so the wash runs heavier than the
          other banners to keep the headline legible. */}
      <PageBanner
        image={ABOUT_COVER}
        alt="Hive Homes office reception with the hexagon brand wall"
        overlay="heavy"
        objectPosition="70% center"
      >
        <p className="eyebrow">About Hive Homes</p>
        <h1 className="mt-5 display-xl max-w-[15ch] text-[var(--text-on-dark)]">
          Dubai's Boutique Real Estate Brokerage.
        </h1>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Link to="/contact" className="btn btn-primary btn-block-sm">
            Speak to Our Team
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </Link>
          <Link to="/properties" className="btn btn-ghost btn-block-sm">
            Browse Properties
          </Link>
        </div>
        <p className="mt-7 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
          <span className="inline-block size-1.5 rounded-full bg-[var(--gold-bright)]" aria-hidden />
          RERA Registered · Licence No. {reraOrn}
        </p>
      </PageBanner>

      {/* Intro copy — kept out of the hero per the client's feedback
          (title + CTA in the header, text below). */}
      <section className="border-b border-[var(--line)]">
        <div className="container-wide py-12 sm:py-14 md:py-16 short:py-8">
          <p className="eyebrow">Who We Are</p>
          <p className="mt-5 max-w-[64ch] text-[15.5px] leading-[1.75] text-[var(--text-soft)] sm:text-[16.5px]">
            Located in the heart of Dubai, <span translate="no">Hive Homes</span>{" "}
            is a real estate brokerage with one clear purpose: to connect
            buyers, sellers, and investors with the right property and to make
            the experience genuinely exceptional — from the first conversation
            to long after the keys are handed over.
          </p>
          <p className="mt-4 max-w-[64ch] text-[15.5px] leading-[1.75] text-[var(--text-soft)] sm:text-[16.5px]">
            We make real estate in Dubai personal, transparent, and worth
            every moment of your time — combining deep market knowledge, a
            curated selection of properties, and a team that genuinely cares.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide grid gap-10 py-14 sm:py-16 md:grid-cols-12 md:gap-14 md:py-20 short:py-10">
          <div className="md:col-span-4">
            <p className="eyebrow">Our Values</p>
            <h2 className="mt-4 display-lg max-w-[12ch] text-[var(--text)]">
              What We Stand For.
            </h2>
          </div>
          <ul className="md:col-span-8">
            {VALUES.map((value) => (
              <li
                key={value.title}
                className="grid gap-3 border-t border-[var(--line)] py-6 sm:grid-cols-[260px_1fr] sm:gap-8 sm:py-7"
              >
                <h3 className="font-display text-[22px] leading-tight text-[var(--text)] sm:text-[26px]">
                  {value.title}
                </h3>
                <p className="text-[14.5px] leading-[1.75] text-[var(--text-soft)] sm:text-[15.5px]">
                  {value.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Team */}
      <Team />


      {/* Closing CTA */}
      <section className="container-wide py-14 sm:py-16 md:py-20 short:py-10">
        <div className="grid gap-8 border-y border-[var(--line)] py-10 md:grid-cols-12 md:items-center md:gap-12 md:py-12">
          <div className="md:col-span-7">
            <p className="eyebrow">Ready to Start?</p>
            <h2 className="mt-4 display-lg max-w-[18ch] text-[var(--text)]">
              Let's find what is right for you.
            </h2>
            <p className="mt-5 max-w-[60ch] text-[15px] leading-[1.75] text-[var(--text-soft)] sm:text-[16px]">
              Whether you are buying, selling, or simply exploring your options,
              our team is ready to listen. No pressure, no obligation. Just an
              honest conversation about what is right for you.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row md:col-span-5 md:justify-end">
            <Link to="/contact" className="btn btn-primary btn-block-sm">
              Speak to Our Team
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </Link>
            <Link to="/properties" className="btn btn-ghost btn-block-sm">
              View Listings
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
